import React from 'react';
import { Plus, Check } from 'lucide-react';
import { fmtPct } from './format';

/** "2.4×" for over-index, "0.4×" for under-index; colour by direction. */
function ratio(index) {
  if (index == null) return { text: 'n/a', tone: 'text-gray-400' };
  const r = index / 100;
  if (r === 0) return { text: 'none', tone: 'text-red-700' };
  const text = r >= 10 ? `${Math.round(r)}×` : r < 0.1 ? `${r.toFixed(2)}×` : `${r.toFixed(1)}×`;
  if (index >= 150) return { text, tone: 'text-green-700' };
  if (index <= 67) return { text, tone: 'text-red-700' };
  return { text, tone: 'text-gray-700' };
}

function MiniBars({ shareConv, shareBase, baselineLabel }) {
  const max = Math.max(1, shareConv || 0, shareBase || 0);
  const w = (v) => `${Math.max(2, Math.round((100 * (v || 0)) / max))}%`;
  return (
    <div className="space-y-1" aria-hidden>
      <div className="flex items-center gap-2 text-[11px] text-gray-600">
        <span className="w-16 shrink-0">converters</span>
        <div className="h-2 flex-1 rounded bg-gray-100 overflow-hidden"><div className="h-full rounded bg-[#0062FB]" style={{ width: w(shareConv) }} /></div>
        <span className="w-9 text-right tabular-nums text-gray-800">{fmtPct(shareConv)}</span>
      </div>
      <div className="flex items-center gap-2 text-[11px] text-gray-500">
        <span className="w-16 shrink-0 truncate">{baselineLabel}</span>
        <div className="h-2 flex-1 rounded bg-gray-100 overflow-hidden"><div className="h-full rounded bg-gray-400/70" style={{ width: w(shareBase) }} /></div>
        <span className="w-9 text-right tabular-nums">{fmtPct(shareBase)}</span>
      </div>
    </div>
  );
}

/**
 * Ranked findings as compact cards: field:value is first, then the
 * ratio headline, with a two-bar strip of the shares.
 * The full sentence lives in the tooltip.
 */
export default function InsightList({ insights, loading, onAddRule, isRuleActive, isRuleElsewhere, onJumpToFeature, hasConverterData = true, baselineLabel = 'baseline' }) {
  if (loading) return <div className="text-sm text-gray-500">Loading insights…</div>;
  if (!insights?.length) {
    return (
      <div className="text-sm text-gray-500">
        {hasConverterData ? 'No significant findings for this baseline.' : 'No converter data for this offer, so nothing to compare yet.'}
      </div>
    );
  }
  return (
    <ol className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
      {insights.map((i) => {
        const active = isRuleActive?.(i);
        const elsewhere = !active && isRuleElsewhere?.(i);
        const r = ratio(i.index);
        const over = i.direction === 'over';
        return (
          <li key={`${i.feature}::${i.value}`} className="rounded-lg border bg-white p-3 flex flex-col gap-2" title={i.text}>
            <div className="flex items-start justify-between gap-2">
              <button
                type="button"
                className="min-w-0 text-sm font-medium text-gray-900 hover:underline truncate text-left"
                title={`${i.display_label}: ${i.value_label}`}
                onClick={() => onJumpToFeature?.(i)}
              >
                {i.display_label}: {i.value_label}
              </button>
              {onAddRule ? (
                <button
                  type="button"
                  onClick={() => onAddRule(i)}
                  className={`shrink-0 inline-flex items-center gap-1 rounded border px-2 py-1 text-xs ${
                    active
                      ? 'border-green-200 text-green-700 bg-green-50'
                      : elsewhere
                        ? 'border-blue-200 text-blue-700 bg-blue-50'
                        : 'border-gray-200 text-gray-700 hover:border-[#0062FB] hover:text-[#0062FB]'
                  }`}
                  aria-label={
                    active
                      ? `Rule added: ${i.display_label} ${i.value_label}`
                      : elsewhere
                        ? `In another group: ${i.display_label} ${i.value_label}`
                        : `Add rule: ${i.display_label} ${i.value_label}`
                  }
                >
                  {active || elsewhere ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                  {active ? 'Added' : elsewhere ? 'In other group' : over ? 'Include' : 'Exclude'}
                </button>
              ) : null}
            </div>
            <div className="min-w-0">
              <div className={`text-2xl font-semibold leading-none tabular-nums ${r.tone}`}>{r.text}</div>
              <div className="mt-1 text-[11px] uppercase tracking-wide text-gray-400">
                {i.share_conv === 0 ? 'no converters' : over ? 'more likely' : 'less likely'}
                {i.lift != null ? ` · ${i.lift > 0 ? '+' : ''}${i.lift.toFixed(0)} pts` : ''}
                {i.significant ? ' · ★' : i.significant === false ? ' · n.s.' : ''}
              </div>
            </div>
            <MiniBars shareConv={i.share_conv} shareBase={i.share_base} baselineLabel={baselineLabel} />
          </li>
        );
      })}
    </ol>
  );
}
